import {
	Component, OnInit, OnDestroy, EventEmitter, ViewEncapsulation, Input, Output, ChangeDetectorRef, ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription } from 'rxjs';
import moment from 'moment';
import to from 'await-to-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import {
	StripeCustomerReponse,
	StripePaymentMethodsResponse,
} from '../../types/responses';
import { RecurringFeatureService } from '../../core/services/data/recurring-feature.service';
import { SitesService } from '../../core/services/data/sites.service';
import { SiteAdvertsService } from '../../core/services/data/site-adverts.service';
import { StripeService } from '../../core/services/stripe.service';
import { ModalService } from '../../core/services/modal.service';
import { environment } from '../../../environments/environment';

import { UserService } from '../../core/services/data/user.service';
import { IPopulatedStripePaymentMethod } from '../../types/populated';
import { SavedPaymentsMethodsService } from '../../core/services/data/saved-payment-methods.service';

type UpcomingPromotion = {
	date: Date;
	pending: boolean;
}

@Component({
	selector: 'app-recurring-promotion',
	templateUrl: './recurring-promotion.component.html',
	styleUrls: ['./recurring-promotion.component.scss'],
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})

export class RecurringPromotionComponent implements OnInit, OnDestroy {
	@Input() public set editRecurring(recurring) {
		if (!recurring) {
			this.editMode = false;
			return;
		}
		this.handleEdit(recurring);
	}

	@Output() public recurringSetup = new EventEmitter();

	public editMode = false;

	public processing = false;

	public upcomingPromotions: UpcomingPromotion[] = [];

	public recurrence: number;

	public feature: any;

	public stripe;

	public elements;

	public stripeCardElement;

	public site: any;

	public siteAdvert: any;

	public sites: any;

	public siteAdverts: any;

	public user: any;

	public showCardForm = false;

	public stripeCustomer: any;

	public stripeSelectedPaymentMethodId: string;

	public stripePrimaryPaymentMethod: any;

	public stripePaymentMethods = [];

	public savedPaymentMethods = [];

	public optedInToSaveCard;

	public priceLimit: string;

	public siteImage: string;

	public showRecurringPromotionOption = new EventEmitter();

	public cardHint;

	public today = moment().toDate();

	public startDate = this.today;

	public confirmationsForm = new FormGroup({
		charge: new FormControl(false, Validators.required),
		bookPricing: new FormControl(false, Validators.required),
	});

	public faArrowsRotate = faArrowsRotate;

	public modalErrorOptions = {
		title: 'Oops!',
		text: ['We were unable to schedule this recurring feature. Please try again later or reach out to support.'],
		actions: [{
			type: 'solid',
			text: 'Got it',
			callback: () => this.modalService.close('recurring-feature-error'),
		}],
	};

	public isSchedulingConflict = false;

	private subscriptions$ = new Subscription();

	constructor(
		private recurringFeatureService: RecurringFeatureService,
		private sitesService: SitesService,
		private siteAdvertsService: SiteAdvertsService,
		private userService: UserService,
		private stripeService: StripeService,
		private savedPaymentMethodsService: SavedPaymentsMethodsService,
		private modalService: ModalService,
		private changeDetector: ChangeDetectorRef,
	) { }

	reset() {
		this.startDate = this.today;
		this.confirmationsForm.reset();
	}

	async ngOnInit() {
		this.stripe = await loadStripe(environment.stripePK);

		this.subscriptions$.add(this.recurringFeatureService.originalFeature$.subscribe((feature: any) => {
			this.feature = feature;

			if (this.sites) {
				this.site = this.sites[this.feature._site];
				this.siteImage = this.site.logoURL;
			}

			if (this.siteAdverts) {
				this.siteAdvert = this.siteAdverts[this.feature._siteAdvert];
				this.determinePriceLimits();
			}
		}));

		this.subscriptions$.add(this.recurringFeatureService.isSchedulingConflict$.subscribe((conflict: any) => {
			this.isSchedulingConflict = conflict;
		}));

		this.subscriptions$.add(this.recurringFeatureService.upcomingPromotions$.subscribe((promotions: UpcomingPromotion[]) => {
			this.upcomingPromotions = promotions;
		}));

		this.sitesService.fetchSites();
		this.siteAdvertsService.fetchSiteAdverts(true);

		this.subscriptions$.add(this.sitesService.sitesById$.subscribe((sites) => {
			this.sites = sites;
			if (this.feature) {
				this.site = sites[this.feature._site];
			}
		}));

		this.subscriptions$.add(this.siteAdvertsService.siteAdvertsById$.subscribe((siteAdverts) => {
			this.siteAdverts = siteAdverts;
			if (this.feature) {
				this.siteAdvert = siteAdverts[this.feature._siteAdvert];
			}
		}));

		this.userService.fetchUser().catch(() => {});

		this.subscriptions$.add(this.userService.user$.subscribe((user) => {
			this.user = user;
			this.handleStripeCustomer();

			this.savedPaymentMethodsService.list(this.user._id).subscribe((data: any) => {
				if (data.success) {
					this.savedPaymentMethods = data.savedPaymentMethods;
					this.getStripePaymentMethods();
				}
			});
		}));

		this._generateStripeForm();
	}

	ngOnDestroy() {
		this.subscriptions$.unsubscribe();
	}

	setCardHint(hint) {
		this.cardHint = hint;
		this.changeDetector.detectChanges();
	}

	handleEdit(recurring) {
		this.editMode = true;
		this.startDate = moment(recurring.pubDate, 'YYYYMMDD').toDate();
		this.confirmationsForm.controls.charge.setValue(true);
		this.setRecurrence(recurring.frequency);
	}

	determinePriceLimits() {
		if (!this.siteAdvert) {
			return;
		}

		if (this.siteAdvert.priceRange.min === 0 && this.siteAdvert.priceRange.max === 0) {
			this.priceLimit = 'Free';
		}

		if (this.siteAdvert.priceRange.min === 0 && this.siteAdvert.priceRange.max > 0) {
			this.priceLimit = `Up to $${this.siteAdvert.priceRange.max}`;
		}

		if (this.siteAdvert.priceRange.min > 0 && this.siteAdvert.priceRange.max > 0) {
			this.priceLimit = `$${this.siteAdvert.priceRange.min} - $${this.siteAdvert.priceRange.max}`;
		}
	}

	selectedStripeCard(paymentMethodId: string) {
		this.stripeSelectedPaymentMethodId = paymentMethodId;
		this.recurringFeatureService.setPaymentInformation(this.user.stripeCustomerId, paymentMethodId, this.user._id);
	}

	handleStripeCustomer() {
		if (this.user.stripeCustomerId) {
			return this.getStripeCustomer();
		}

		return this.checkStripeCustomer();
	}

	getStripePaymentMethods() {
		// If payments are already loaded, if there are no saved payments, or if the user has not opted into saved payments, return.

		if (!this.savedPaymentMethods?.length || this.savedPaymentMethods.length === 0) {
			return;
		}

		this.stripeService.getCustomerPaymentMethods(this.user.stripeCustomerId).subscribe((result: StripePaymentMethodsResponse) => {
			if (!result.success || !result.paymentMethods) {
				return;
			}

			const savedPaymentMethodIds = this.savedPaymentMethods.map((paymentMethod) => paymentMethod.paymentMethodId);
			const paymentMethods = result.paymentMethods.filter((item) => savedPaymentMethodIds.includes(item.id));

			const [primary] = paymentMethods;
			this.stripePrimaryPaymentMethod = primary;

			if (primary) {
				this.stripePaymentMethods = paymentMethods.filter(
					(paymentMethod: IPopulatedStripePaymentMethod) => paymentMethod.id !== primary.id,
				);

				this.recurringFeatureService.setPaymentInformation(this.user.stripeCustomerId, primary.id, this.user._id);
			} else {
				this.stripePaymentMethods = paymentMethods;
			}
		});
	}

	checkStripeCustomer(): Promise<string> {
		return new Promise((resolve) => {
			this.stripeService.createStripeCustomer(this.user.username).subscribe((result: StripeCustomerReponse) => {
				if (!result.success) return resolve(null);

				this.user.stripeCustomerId = result.customer.id;
				this.stripeCustomer = result.customer;

				const fieldsToUpdate = {
					stripeCustomerId: result.customer.id,
				};

				this.userService.saveProfile(this.user._id, fieldsToUpdate).subscribe();
				this.userService.updateUser(this.user);

				return resolve(result.customer.id);
			});
		});
	}

	getStripeCustomer(): Promise<string> {
		return new Promise((resolve) => {
			this.stripeService.getCustomer(this.user.stripeCustomerId).subscribe((result: StripeCustomerReponse) => {
				if (!result.success) return resolve(null);

				this.stripeCustomer = result.customer;
				return resolve(result.customer.id);
			});
		});
	}

	async schedule() {
		if (this.confirmationsForm.get('charge').value === false) {
			return;
		}

		this.processing = true;

		if (!this.stripeSelectedPaymentMethodId) {
			await this.createPaymentMethod();

			if (!this.stripeSelectedPaymentMethodId) {
				return;
			}
		}

		this.recurringFeatureService.schedule().subscribe((result: any) => {
			if (result.success) {
				this.recurringSetup.emit(result.recurringFeature);
				this.modalService.close('recurring-promotion');
			} else {
				this.modalService.open('recurring-feature-error');
			}

			this.processing = false;
		});
	}

	async createPaymentMethod() {
		const { stripeCardElement: card } = this;

		const [error, result]: [any, any] = await to(
			this.stripe.createPaymentMethod({
				type: 'card',
				card,
			}),
		);

		if (error) {
			this.modalService.open('stripe-error');
			return;
		}

		if (result.error) {
			this.modalService.open('stripe-error');
			return;
		}

		this.stripeSelectedPaymentMethodId = result.paymentMethod.id;

		this.recurringFeatureService.setPaymentInformation(this.user.stripeCustomerId, result.paymentMethod.id, this.user._id);

		const paymentMethodId = typeof result.paymentMethod === 'string' ? result.paymentMethod : result.paymentMethod.id;
		this.savedPaymentMethodsService.create({
			_user: this.user._id,
			paymentMethodId,
			attachToCustomer: true,
			customerId: this.user.stripeCustomerId,
		}).subscribe();

		this.savedPaymentMethodsService.list(this.user._id).subscribe((data: any) => {
			if (data.success) {
				this.savedPaymentMethods = data.savedPaymentMethods;
				this.getStripePaymentMethods();
			}
		});
	}

	async _generateStripeForm() {
		const appearance = {
			theme: 'flat',
			variables: { colorPrimaryText: '#262626' },
		};

		const cardOptions = {
			style: {
				base: {
					fontSize: '16px',
				},
			},
		};
		this.elements = this.stripe.elements({ appearance });
		this.stripeCardElement = this.elements.create('card', cardOptions);
		this.stripeCardElement.mount('#stripe-card');
	}

	close() {
		this.recurringFeatureService.close();
		this.reset();
	}

	setRecurrence(frequency: number) {
		this.recurrence = frequency;
		this.recurringFeatureService.setFrequency(frequency);
		this.determineUpcomingPromotions();
	}

	handleDateChange(date: Date) {
		this.startDate = date;
		this.determineUpcomingPromotions();
		this.recurringFeatureService.setPubDate(+moment(date).format('YYYYMMDD'));
		this.recurringFeatureService.checkSchedule();
	}

	determineUpcomingPromotions() {
		const next = moment(this.startDate).add(this.recurrence, 'days');
		const following = moment(next).add(this.recurrence, 'days');
		const last = moment(following, 'YYYYMMDD').add(this.recurrence, 'days');

		this.upcomingPromotions = [{
			date: next.toDate(),
			pending: true,
		}, {
			date: following.toDate(),
			pending: true,
		}, {
			date: last.toDate(),
			pending: true,
		}];
	}
}
